// Render-decoupled store (002 contracts/core-engine.md, Constitution Principle III).
//
// The single authoritative match state lives here, OUTSIDE the React render tree.
// Display components subscribe and derive; they never own rule logic. An optional
// SyncTransport mirrors state to other windows: the main window broadcasts its changes;
// secondary windows apply incoming patches without re-broadcasting (no echo loop).

export function createStore(initialState, { transport = null, isMain = true } = {}) {
  let state = initialState;
  const listeners = new Set();
  let applyingRemote = false;

  function emitChange() {
    for (const l of [...listeners]) l(state);
  }

  function setState(updater) {
    const next = typeof updater === 'function' ? updater(state) : updater;
    if (next === state) return;
    state = next;
    emitChange();
    if (transport && isMain && !applyingRemote) transport.broadcast(state);
  }

  if (transport) {
    transport.onPatch((remoteState) => {
      applyingRemote = true;
      state = remoteState;
      emitChange();
      applyingRemote = false;
    });

    // Snapshot handshake (optional — older transports omit these methods, so the calls no-op).
    // The main only broadcasts on CHANGE, so a secondary window that connects after the operator
    // has already prepared the board would otherwise stay empty until the next change. To fix that:
    //   • the MAIN answers a snapshot request by re-broadcasting its CURRENT state, and
    //   • a SECONDARY asks for that snapshot once on connect.
    if (isMain) {
      transport.onRequest?.(() => {
        if (transport.broadcast) transport.broadcast(state);
      });
    } else {
      transport.requestSnapshot?.();
    }
  }

  return {
    getState: () => state,
    setState,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
